import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    generatePath,
    useNavigate,
    useLocation,
    Location,
    Link,
} from 'react-router-dom';

import {
    QUESTIONNAIRE_PATH,
    SIGN_IN_TOKEN_PATH,
} from '../../../constants/route.constants';
import ProjectActions from '../../../participant/actions/ProjectActions';
import ProjectSelector from '../../../participant/selectors/ProjectSelector';
import IQuestionnaire from '../../../types/survey/IQuestionnaire';
import IProject from '../../../types/IProject';
import ConsentDialog from '../../../components/dialog/ConsentDialog/ConsentDialog';
import useScrollToTop from '../../../hooks/useScrollToTop';
import QuestionnaireCard from '../../../components/cards/QuestionnaireCard/QuestionnaireCard';
import ProjectsPageLayout from '../../../components/layout/ProjectsPageLayout/ProjectsPageLayout';
import ProjectsSection from '../../../components/projects/ProjectsSection/ProjectsSection';
import NoData from '../../../components/NoData/NoData';
import { Translate, useTranslate } from 'react-translate.ts';
import { SurveyTimerSelectors } from '../../../common/selectors/SurveyTimerSelectors';
import { AppDispatch } from '../../../reducers/store';
import LanguageDialog from '../../../components/dialog/LanguageDialog/LanguageDialog';
import LanguageSlice from '../../../common/slices/LanguageSlice';
import DashboardSlice, {
    type DashboardState,
} from '../../../participant/slices/DashboardSlice';
import { AppState } from '../../../reducers/root.reducer';
import Demographics from '../../../common/components/Demographics/Demographics';
import { Button, ButtonVariants, Card } from '@tcc-online/common-components';
import UserSlice from '../../../common/slices/UserSlice';
import styles from './DashboardPage.module.scss';
import AuthSelectors from '../../../selectors/AuthSelectors';

export default function DashboardPage() {
    useScrollToTop();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const locale = useSelector(LanguageSlice.state.getLocale);
    const { data: projects, loading: projectsLoading } =
        ProjectSelector.useGetProjects();
    const { loading: consentLoading, success: consentSuccess } =
        ProjectSelector.useGetConsent();
    const translate = useTranslate();
    const { data: userInfo } = useSelector(UserSlice.state.info);

    const dashboardState = useSelector(DashboardSlice.state.all);
    const isAnonymous = useSelector(AuthSelectors.isAnonymous);

    useEffect(() => {
        dispatch(ProjectActions.getProjects());
    }, [dispatch]);

    useEffect(() => {
        const project = projects.find(
            (project) => dashboardState.project?.id === project.id,
        );
        if (project) {
            dispatch(DashboardSlice.actions.setProject({ project }));
        }
    }, [projects, locale]);

    const langIsSupported = useSelector((state: AppState) =>
        DashboardSlice.state.questionnaireHasSupportedLanguage(state, locale),
    );

    useEffect(() => {
        if (
            (dashboardState.project?.consent === true && langIsSupported) ||
            (!consentLoading &&
                consentSuccess &&
                dashboardState.consent.sent &&
                dashboardState.questionnaire?.id &&
                langIsSupported)
        ) {
            if (dashboardState.questionnaire) {
                navigate(
                    generatePath(QUESTIONNAIRE_PATH, {
                        id: dashboardState.questionnaire.id.toString(),
                    }),
                    { relative: 'path' },
                );
                dispatch(DashboardSlice.actions.clear());
            }
        }
    }, [
        consentLoading,
        consentSuccess,
        dashboardState.consent.sent,
        dashboardState.questionnaire?.id,
        history,
        langIsSupported,
        dashboardState.project?.consent,
    ]);

    useEffect(() => {
        return () => {
            dispatch(DashboardSlice.actions.clear());
        };
    }, []);

    const location = useLocation() as Location<{ questionnaireId?: number }>;
    useEffect(() => {
        if (location?.state?.questionnaireId) {
            const data = findProjectWithQuestionnaire(
                projects,
                location.state.questionnaireId,
            );

            if (data && locale) {
                openQuestionnaire(
                    data.project,
                    data.questionnaire,
                    locale,
                    showConsentDialog,
                    showLangDialog,
                    (path: string) => navigate(path, { relative: 'path' }),
                );
            }
        }
    }, [projects, location?.state?.questionnaireId]);

    return (
        <ProjectsPageLayout>
            <Demographics />
            {renderDialog(dashboardState, consentLoading)}
            {userInfo?.anonymousToken && (
                <Card className={styles.anonym}>
                    <div>{translate('fe.participant.anonymous.info')}</div>
                    <div className={styles.link}>
                        <Link
                            to={generatePath(SIGN_IN_TOKEN_PATH, {
                                token: userInfo.anonymousToken,
                                params: null,
                            })}
                            relative="path"
                        >
                            <Button variant={ButtonVariants.positive}>
                                {translate('fe.participant.anonymous.button')}
                            </Button>
                        </Link>
                    </div>
                </Card>
            )}
            {render(projects, projectsLoading)}
        </ProjectsPageLayout>
    );

    function renderDialog(dashboardState: DashboardState, loading) {
        if (dashboardState.language.visible) {
            return (
                <LanguageDialog
                    options={dashboardState.questionnaire?.languages}
                    isVisible
                    onClose={closeConsentDialog}
                    onConfirm={confirmLangDialog}
                />
            );
        }

        if (dashboardState.consent.visible) {
            return (
                <ConsentDialog
                    text={dashboardState.project?.consentContent?.[0]}
                    moreText={dashboardState.project?.consentContent?.[1]}
                    isVisible
                    onClose={closeConsentDialog}
                    onConfirm={confirmConsentDialog}
                    isLoading={loading}
                />
            );
        }
    }

    function render(projects: IProject[], loading: boolean) {
        if (loading) {
            return <Section isLoading={loading} />;
        }

        if (projects?.length === 0) {
            return (
                <NoData title={translate('fe.todo.list.empty')}>
                    {!isAnonymous && (
                        <Translate textOnly>fe.todo.list.results</Translate>
                    )}
                </NoData>
            );
        }

        return projects.map((item: IProject, i: number) => (
            <Section
                key={i}
                project={item}
                onShowDialog={showConsentDialog}
                onShowLangDialog={showLangDialog}
                isLoading={false}
            />
        ));
    }

    function showConsentDialog(
        project: IProject,
        questionnaire: IQuestionnaire,
    ) {
        if (consentLoading) {
            return;
        }

        dispatch(
            DashboardSlice.actions.showConsent({ project, questionnaire }),
        );
    }

    function closeConsentDialog() {
        if (consentLoading) {
            return;
        }

        dispatch(DashboardSlice.actions.closeDialog());
    }

    function confirmConsentDialog() {
        if (consentLoading || !dashboardState.project) {
            return;
        }

        dispatch(ProjectActions.giveConsent(dashboardState.project.id));
        dispatch(DashboardSlice.actions.confirmConsentDialog());
    }

    function showLangDialog(project: IProject, questionnaire: IQuestionnaire) {
        dispatch(
            DashboardSlice.actions.showLanguage({ project, questionnaire }),
        );
    }

    function confirmLangDialog(locale: string) {
        dispatch(LanguageSlice.actions.changeLocale(locale));
        dispatch(DashboardSlice.actions.confirmLanguageDialog());
    }
}

type SectionProps = {
    project?: IProject;
    isLoading?: boolean;
    onShowDialog?: (project: IProject, questionnaire: IQuestionnaire) => void;
    onShowLangDialog?: (
        project: IProject,
        questionnaire: IQuestionnaire,
    ) => void;
};

function Section(props: SectionProps) {
    const navigate = useNavigate();
    const { project, isLoading, onShowDialog, onShowLangDialog } = props;
    const currentQuestionnaireId = useSelector(
        SurveyTimerSelectors.getCurrentId,
    );
    const locale = useSelector(LanguageSlice.state.getLocale);

    return (
        <ProjectsSection
            name={project?.name}
            isLoading={!project?.questionnaires || isLoading}
        >
            {project?.questionnaires?.map((item: IQuestionnaire, i: number) => (
                <QuestionnaireCard
                    key={i}
                    item={item}
                    onClick={handleClick(project)}
                    disabled={
                        !!currentQuestionnaireId &&
                        currentQuestionnaireId !== item.id
                    }
                />
            ))}
        </ProjectsSection>
    );

    function handleClick(project: IProject) {
        return (questionnaire: IQuestionnaire) => {
            if (locale) {
                openQuestionnaire(
                    project,
                    questionnaire,
                    locale,
                    onShowDialog,
                    onShowLangDialog,
                    (path: string) => navigate(path, { relative: 'path' }),
                );
            }
        };
    }
}

function openQuestionnaire(
    project: IProject,
    questionnaire: IQuestionnaire,
    locale: string,
    onShowConsent?: (project: IProject, questionnaire: IQuestionnaire) => void,
    onShowLanguage?: (project: IProject, questionnaire: IQuestionnaire) => void,
    onShowQuestionnaire?: (path: string) => void,
) {
    if (!questionnaire.languages.includes(locale.toLowerCase())) {
        onShowLanguage?.(project, questionnaire);
    } else if (project.consent === false) {
        onShowConsent?.(project, questionnaire);
    } else {
        onShowQuestionnaire?.(
            generatePath(QUESTIONNAIRE_PATH, {
                id: questionnaire.id.toString(),
            }),
        );
    }
}

function findProjectWithQuestionnaire(
    projects: IProject[],
    questionnaireId: number,
): { project: IProject; questionnaire: IQuestionnaire } | null {
    for (const project of projects) {
        for (const questionnaire of project.questionnaires) {
            if (questionnaireId === questionnaire.id) {
                return { project, questionnaire };
            }
        }
    }

    return null;
}
